import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit"
import { RootState } from "./app/store"
import dayjs, { Dayjs } from "dayjs"

// Store dates as ISO strings for Redux serialization
type DateValue = string

export interface GlobalRptFilters {
  business_unit: string[]
  supplier: string[]
  invoice_received_start: DateValue | null
  invoice_received_end: DateValue | null
  invoice_generation_start: DateValue | null
  invoice_generation_end: DateValue | null
  // Date ranges: null when not set, [start, end] when both dates present
  invoiceReceiveDate: [DateValue, DateValue] | null
  invoiceGenerationDate: [DateValue, DateValue] | null
}

// Local filters per tab
export interface InvoiceOpsLocalFilters {
  invoice_status?: string[] | null
  invoice_type?: string[] | null
  rejection_type?: string[] | null
  rejection_reason?: string[] | null
}

export interface InvoiceComplexityLocalFilters {
  complexity_type?: string[] | null
  invoice_status?: string[] | null
}

export interface SupplierLocalFilters {
  invoice_status?: string[] | null
  complexity_type?: string[] | null
  rejection_type?: string[] | null
}

export interface BusinessUnitLocalFilters {
  invoice_status?: string[] | null
  complexity_type?: string[] | null
  rejection_type?: string[] | null
}

export interface TeamReviewerLocalFilters {
  team?: string[] | null
  complexity_type?: string[] | null
  manual_review_outcome?: string[] | null
}

export interface TabLocalFilters {
  0: InvoiceOpsLocalFilters
  1: InvoiceComplexityLocalFilters
  2: SupplierLocalFilters
  3: BusinessUnitLocalFilters
  4: TeamReviewerLocalFilters
}

export interface ReportingState {
  globalRptFilters: GlobalRptFilters
  tabLocalFilters: TabLocalFilters
}

const startOfMonth = dayjs().startOf("month").toISOString()
const currentDate = dayjs().toISOString()

const initialState: ReportingState = {
  globalRptFilters: {
    business_unit: [],
    supplier: [],
    invoice_received_start: null,
    invoice_received_end: null,
    invoice_generation_start: null,
    invoice_generation_end: null,
    invoiceReceiveDate: [startOfMonth, currentDate],
    invoiceGenerationDate: null,
  },
  tabLocalFilters: {
    0: {},
    1: {},
    2: {},
    3: {},
    4: {},
  },
}

// Helper to convert dayjs/Date objects to ISO strings for date range fields
const DATE_RANGE_FIELDS = ['invoiceReceiveDate', 'invoiceGenerationDate'] as const

type DateRangeInput = [Dayjs, Dayjs] | [string, string] | null

const toISOString = (date: Dayjs | string): string => {
  if (typeof date === 'string') return date
  return date.toISOString()
}

const convertDatesToISO = (payload: Partial<GlobalRptFilters>): Partial<GlobalRptFilters> => {
  const converted = { ...payload }

  DATE_RANGE_FIELDS.forEach(field => {
    if (!(field in converted)) return

    const value = converted[field] as DateRangeInput
    if (!Array.isArray(value)) {
      converted[field] = null
      return
    }

    converted[field] = [toISOString(value[0]), toISOString(value[1])]
  })

  return converted
}

export const reportingSlice = createSlice({
  name: "reporting",
  initialState,
  reducers: {
    setGlobalRptFilters: {
      reducer(state, action: PayloadAction<GlobalRptFilters>) {
        state.globalRptFilters = action.payload
      },
      prepare(payload: GlobalRptFilters) {
        return { payload: convertDatesToISO(payload) as GlobalRptFilters }
      },
    },
    updateGlobalRptFilters: {
      reducer(state, action: PayloadAction<Partial<GlobalRptFilters>>) {
        state.globalRptFilters = { ...state.globalRptFilters, ...action.payload }
      },
      prepare(payload: Partial<GlobalRptFilters>) {
        return { payload: convertDatesToISO(payload) }
      },
    },
    setTabLocalFilters: (state, action: PayloadAction<TabLocalFilters>) => {
      state.tabLocalFilters = action.payload
    },
    updateTabLocalFilter: (
      state,
      action: PayloadAction<{
        tab: keyof TabLocalFilters
        filters: TabLocalFilters[keyof TabLocalFilters]
      }>,
    ) => {
      state.tabLocalFilters[action.payload.tab] = action.payload.filters
    },
    resetReportingState: () => initialState,
  },
})

export const {
  setGlobalRptFilters,
  updateGlobalRptFilters,
  setTabLocalFilters,
  updateTabLocalFilter,
  resetReportingState,
} = reportingSlice.actions

export const selectGlobalRptFilters = (state: RootState) =>
  state.reporting.globalRptFilters

export const selectTabLocalFilters = (state: RootState) =>
  state.reporting.tabLocalFilters

// Memoized selector to convert ISO strings back to dayjs for UI consumption
export const selectGlobalRptFiltersWithDayjs = createSelector(
  selectGlobalRptFilters,
  (filters) => ({
    ...filters,
    invoiceReceiveDate: filters.invoiceReceiveDate
      ? [dayjs(filters.invoiceReceiveDate[0]), dayjs(filters.invoiceReceiveDate[1])]
      : null,
    invoiceGenerationDate: filters.invoiceGenerationDate
      ? [dayjs(filters.invoiceGenerationDate[0]), dayjs(filters.invoiceGenerationDate[1])]
      : null,
  })
)

export default reportingSlice.reducer
